<template>
    <Frame
        v-if="$slots.default || notification || loading"
        class="notification d-flex align-items-center justify-content-start"
        :class="[colorBuffer ? classes : 'bg-white', hasHover ? 'has--hover' : null]"
        :shadow="shadow"
        transparent
    >
        <div v-if="loading" class="flex-grow-1 text-center">
            <Spinner />
        </div>
        <template v-else>
            <div v-if="iconBuffer && !disableIcon" class="notification-prefix pe-1">
                <Icon :name="iconBuffer" :size="iconSize" />
            </div>
            <div
                :class="`notification-msg py-1 ${iconBuffer && !disableIcon ? 'ps-2' : ''}
                ${unread ? 'fw-semibold' : ''}`"
            >
                <template v-if="$slots.default"><slot></slot></template>
                <template v-else-if="notification">
                    <template v-if="notification.message.title && notification.message.body">
                        <h4 class="text-sm fw-medium mb-1">{{ notification.message.title }}</h4>
                        <p class="text-xs">{{ notification.message.body }}</p>
                    </template>
                    <template v-else>
                        <p>
                            {{ $t(`ui.notifications.types.${notification.type}`) }}

                            <!-- Last four -->
                            <template v-if="notification.data.last_four">
                                <br />
                                {{ $t('ui.notifications.data.number') }} ••••
                                {{ notification.data.last_four }}
                            </template>
                            <template v-else-if="notification.data.card && notification.data.card.last_four">
                                <br />
                                {{ $t('ui.notifications.data.number') }} ••••
                                {{ notification.data.card.last_four }}
                            </template>

                            <!-- Merchant name -->
                            <template v-if="notification.data.partner">
                                <br />
                                {{ $t('ui.notifications.data.merchant') }}
                                {{ notification.data.partner }}
                            </template>
                            <template v-else-if="notification.data.merchant && notification.data.merchant.name">
                                <br />{{ $t('ui.notifications.data.merchant') }}
                                {{ notification.data.merchant.name }}
                            </template>

                            <!-- Wallets -->
                            <template v-if="notification.data.name">
                                <br />
                                {{ $t('ui.notifications.data.wallet') }} {{ notification.data.name }}
                            </template>
                            <template
                                v-else-if="
                                    notification.data.payment_methods &&
                                    notification.data.payment_methods.wallets &&
                                    notification.data.payment_methods.wallets.types
                                "
                            >
                                <br />
                                {{ $t('ui.notifications.data.wallet') }} {{ typesFormatted }}
                            </template>

                            <!-- Amount -->
                            <template v-if="notification.data.amount">
                                <br />
                                {{ $t('ui.notifications.data.amount') }}
                                <Currency :amount="notification.data.amount" />
                            </template>

                            <!-- Overflow amount -->
                            <template
                                v-if="
                                    notification.data.payment_methods &&
                                    notification.data.payment_methods.overflow &&
                                    notification.data.payment_methods.overflow.amount_selected
                                "
                            >
                                <br />
                                {{ $t('ui.notifications.data.overflow') }}
                                <Currency :amount="notification.data.payment_methods.overflow.amount_selected" />
                            </template>

                            <!-- Expire in (days) -->
                            <template v-if="notification.data.expire_in">
                                <br />
                                {{ $t('ui.notifications.data.expire') }} {{ notification.data.expire_in }}
                                {{ $tc('ui.notifications.data.days', notification.data.expire_in) }}
                            </template>

                            <!-- Reason -->
                            <template v-if="notification.data.reason">
                                <br />
                                {{ $t('ui.notifications.data.reason') }}
                                <template v-if="$te(`shared.reasons.${notification.data.reason}`)">
                                    {{ $t(`shared.reasons.${notification.data.reason}`) }}
                                </template>
                                <template v-else>{{ notification.data.reason }}</template>
                            </template>

                            <!-- Transaction at -->
                            <template
                                v-if="
                                    notification.data.card_authorization &&
                                    notification.data.card_authorization.transaction_at
                                "
                            >
                                <br />
                                {{ $t('ui.notifications.data.transaction_at') }}
                                <Timestamp :time="notification.data.card_authorization.transaction_at" />
                            </template>
                        </p>
                    </template>
                </template>
                <p v-if="date" class="fw-normal m-0 pt-1"><Timestamp :time="date" class="text-xxs text-black-40" /></p>
            </div>
        </template>
    </Frame>
</template>

<script>
    import Currency from '../Currency'
    import Frame from '../Frame'
    import Spinner from '../Spinner'
    import Timestamp from '../Timestamp'

    export default {
        name: 'Notification',
        components: {
            Currency,
            Frame,
            Spinner,
            Timestamp,
        },
        props: {
            color: {
                type: String,
                default: null,
                validator: function (value) {
                    return ['success', 'danger'].includes(value)
                },
            },
            date: String,
            disableIcon: {
                type: Boolean,
                default: false,
            },
            hasHover: {
                type: Boolean,
                default: false,
            },
            icon: String,
            iconSize: {
                type: Number,
                default: 20,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            notification: Object,
            shadow: {
                type: Boolean,
                default: true,
            },
            unread: {
                type: Boolean,
                default: false,
            },
            useColor: {
                type: Boolean,
                default: true,
            },
        },
        computed: {
            classes() {
                return {
                    'alert alert-success border-0': this.colorBuffer === 'success',
                    'alert alert-danger border-0': this.colorBuffer === 'danger',
                }
            },
            colorBuffer() {
                if (this.color) {
                    return this.color
                } else if (!this.color && this.notification) {
                    switch (this.notification.type) {
                        case 'card_authorization_approved_to_user_notification':
                        case 'expense_request_confirmed_to_user_notification':
                            return this.useColor ? 'success' : null
                        case 'card_authorization_declined_to_user_notification':
                        case 'card_transaction_debit_failed_to_admin_notification':
                        case 'expense_request_declined_to_user_notification':
                            return this.useColor ? 'danger' : null
                        default:
                            return null
                    }
                } else {
                    return null
                }
            },
            iconBuffer() {
                if (this.icon) {
                    return this.icon
                } else if (!this.icon && this.notification) {
                    switch (this.notification.type) {
                        case 'card_created_to_user_notification':
                        case 'physical_card_created_to_user_notification':
                        case 'virtual_card_created_to_user_notification':
                            return 'card'
                        case 'card_authorization_approved_to_user_notification':
                        case 'expense_request_confirmed_to_user_notification':
                            return 'checked'
                        case 'card_authorization_declined_to_user_notification':
                        case 'card_transaction_debit_failed_to_admin_notification':
                        case 'expense_request_declined_to_user_notification':
                            return 'declined'
                        case 'wallet_reload_notification':
                            return 'wallet'
                        default:
                            return 'bell'
                    }
                } else {
                    return ''
                }
            },
            typesFormatted() {
                return this.notification.data.payment_methods.wallets.types.join(', ')
            },
        },
    }
</script>
